import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom } from 'rxjs';
import { LoggerService } from './logger.service';
import { ErrorHandlerService } from './error-handler.service';
import { NetworkService } from './network.service';
import { OfflineQueueService, QueuedRequestMetadata } from './offline-queue.service';
import { ApiRequest, ApiResponse, BuyCreditsResponseData } from '../models/api.models';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private http = inject(HttpClient);
  private logger = inject(LoggerService);
  private errorHandler = inject(ErrorHandlerService);

  private readonly baseUrl = environment.apiUrl;
  private readonly networkService = inject(NetworkService);
  private readonly offlineQueue = inject(OfflineQueueService);

  /**
   * Send a POST request with FormData to the API
   * 
   * Automatically converts plain objects to FormData and handles errors centrally.
   * All API responses follow the ApiResponse<T> structure with STATUS, DATA, and optional DEBUG fields.
   * 
   * If the device is offline and the request is queueable (e.g., createJob, editUser),
   * the request will be automatically queued for later synchronization.
   * 
   * @param data - Either a FormData object or a plain object that will be converted to FormData.
   *               Plain objects should implement ApiRequest interface (must have taskID property).
   * @param urlEncodedHeaders - Optional Content-Type header (e.g., 'application/x-www-form-urlencoded').
   *                            Defaults to empty string (multipart/form-data).
   * @param queueMetadata - Optional metadata for offline queue (description, page) if request gets queued.
   * @returns Promise resolving to ApiResponse<T> containing:
   *   - STATUS: Response status string ('OK', 'ERROR', etc.)
   *   - DATA: Typed response data (generic type T)
   *   - DEBUG: Optional debug information (runtime, memory)
   * 
   * @throws {Error} If HTTP request fails or network error occurs (and request is not queueable)
   * 
   * @example
   * ```typescript
   * // With typed response
   * const response = await apiService.post<StatusResponse>({
   *   taskID: 'status',
   *   auth_secret: 'token'
   * });
   * 
   * // With FormData
   * const formData = new FormData();
   * formData.append('taskID', 'upload');
   * formData.append('file', file);
   * const response = await apiService.post(formData);
   * 
   * // With queue metadata (for offline queue)
   * const response = await apiService.post(
   *   { taskID: 'createJob', jobName: 'Test Job', ... },
   *   '',
   *   { description: 'Create new job', page: 'invitations' }
   * );
   * ```
   */
  async post<T = unknown>(
    data: ApiRequest | FormData,
    urlEncodedHeaders: string = '',
    queueMetadata?: QueuedRequestMetadata
  ): Promise<ApiResponse<T>> {
    let formData: FormData;
    let taskID: string;

    if (data instanceof FormData) {
      formData = data;
      taskID = formData.get('taskID') as string || '';
    } else if (typeof data === 'object') {
      formData = new FormData();
      Object.entries(data).forEach(([key, value]) => formData.append(key, String(value)));
      taskID = data.taskID || '';
    } else {
      throw new Error('Invalid data type. Expected FormData or object.');
    }

    // Check if offline and request is queueable
    if (this.networkService.isOffline() && taskID) {
      try {
        const queueId = await this.offlineQueue.queueRequest(data, urlEncodedHeaders, queueMetadata);
        this.logger.log(`Request queued for offline processing: ${taskID} (Queue ID: ${queueId})`);
        
        // Return a promise that resolves with a queued response
        // This allows the caller to handle it as if it was successful
        // The actual request will be processed when online
        return {
          STATUS: 'QUEUED',
          DATA: {
            queueId,
            message: 'Request wurde zur Offline-Queue hinzugefügt und wird automatisch synchronisiert, sobald Sie wieder online sind.'
          } as any,
          DEBUG: { runtime: '0', memory: '0' }
        };
      } catch (queueError) {
        // If queueing fails, fall through to normal error handling
        this.logger.warn(`Failed to queue request: ${queueError}`);
      }
    }

    try {
      const headers: { [key: string]: string } = {};
      if (urlEncodedHeaders) {
        headers['Content-Type'] = urlEncodedHeaders;
      }

      const response = await firstValueFrom(
        this.http.post<any>(this.baseUrl, formData, {
          headers,
          withCredentials: false
        }).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.error instanceof ErrorEvent) {
              // Client-side error
              throw new Error(`HTTP-Fehler: ${error.error.message}`);
            } else {
              // Server-side error
              throw new Error(`HTTP-Fehler ${error.status}: ${error.statusText}`);
            }
          })
        )
      );

      // HttpClient automatically parses JSON responses
      return response;
    } catch (error) {
      // Check if offline and provide better error message
      if (this.networkService.isOffline()) {
        this.logger.warn('POST request failed: Device is offline');
        // Service Worker may have cached response, but POST requests shouldn't be cached
        // Throw a network error that can be handled by the caller
        const offlineError = new Error('Network error: Device is offline. Please check your internet connection.');
        this.errorHandler.handleApiError('POST', offlineError);
        throw offlineError;
      }
      
      // Use centralized error handler
      this.errorHandler.handleApiError('POST', error);
      throw error;
    }
  }

  /**
   * Send a GET request with query parameters to the API.
   *
   * Converts the params object to URL query parameters. All values are stringified;
   * for arrays or objects (e.g. buyCredits `items`) pass a JSON string.
   * Responses follow ApiResponse&lt;T&gt;: STATUS, DATA, and optional DEBUG.
   *
   * @param params - Request parameters (must include taskID). Keys become query parameter names,
   *   values are sent as strings. Use JSON.stringify() for complex values like `items`.
   * @returns Promise resolving to ApiResponse&lt;T&gt; with STATUS, DATA (typed by T), and optional DEBUG.
   * @throws {Error} On HTTP or network failure (handled via ErrorHandlerService).
   * @see BuyCreditsResponseData For buyCredits response shape (DATA).
   *
   * @example
   * ```ts
   * // buyCredits: multiple packages per order (items = JSON array of { qty, amount })
   * const items = [{ qty: 20, amount: 440 }, { qty: 50, amount: 900 }];
   * const response = await apiService.get<BuyCreditsResponseData>({
   *   taskID: 'buyCredits',
   *   items: JSON.stringify(items),
   *   paypal_order_id: orderId,
   *   auth_secret: authSecret
   * });
   * ```
   *
   * @example
   * ```ts
   * // Legacy buyCredits: single package (qty + amount instead of items)
   * const response = await apiService.get({
   *   taskID: 'buyCredits',
   *   qty: 200,
   *   amount: '440.00',
   *   paypal_order_id: orderId,
   *   auth_secret: authSecret
   * });
   * ```
   *
   * @example
   * ```ts
   * // Other GET tasks (e.g. status, getInvoices)
   * const response = await apiService.get<StatusResponse>({
   *   taskID: 'status',
   *   auth_secret: authSecret
   * });
   * ```
   */
  async get<T = unknown>(params: ApiRequest): Promise<ApiResponse<T>> {
    try {
      // Build query string from params object
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          queryParams.append(key, String(value));
        }
      });

      const url = `${this.baseUrl}?${queryParams.toString()}`;

      const response = await firstValueFrom(
        this.http.get<any>(url, {
          withCredentials: false
        }).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.error instanceof ErrorEvent) {
              // Client-side error
              throw new Error(`HTTP-Fehler: ${error.error.message}`);
            } else {
              // Server-side error
              throw new Error(`HTTP-Fehler ${error.status}: ${error.statusText}`);
            }
          })
        )
      );

      // HttpClient automatically parses JSON responses
      // Note: Service Worker will handle caching automatically for GET requests
      // If offline, Service Worker will return cached response if available
      return response;
    } catch (error) {
      // Check if offline and provide better error message
      if (this.networkService.isOffline()) {
        this.logger.warn('GET request failed: Device is offline. Service Worker should provide cached response if available.');
        // For GET requests, Service Worker should handle caching
        // But if no cache is available, throw a network error
        const offlineError = new Error('Network error: Device is offline. No cached data available.');
        this.errorHandler.handleApiError('GET', offlineError);
        throw offlineError;
      }
      
      // Use centralized error handler
      this.errorHandler.handleApiError('GET', error);
      throw error;
    }
  }
}
